import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, useInView, useAnimation, Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Star, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Calendar, 
  Users, 
  Award, 
  Shield, 
  CheckCircle, 
  Clock, 
  BookOpen, 
  TrendingUp, 
  Eye, 
  Heart, 
  Share2, 
  ExternalLink, 
  ArrowLeft, 
  Loader2, 
  Briefcase, 
  UserCheck, 
  FileText, 
  Download,
  ChevronRight,
  ChevronDown,
  MessageCircle,
  ThumbsUp,
  X,
  XCircle,
  Facebook,
  Linkedin,
  Building2,
  Target,
  Zap,
  Play,
  Filter,
  ChevronUp,
  User,
  GraduationCap,
  DollarSign,
  Clock3,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { consultantsService, Consultant, ConsultantService } from '../../services/consultants.service';
import { trainingsService } from '../../services/trainings.service';
import { useAuth } from '../../context/AuthContext';
import creneauxService, { Creneau } from '../../services/creneaux.service';
import { API_ENDPOINTS } from '../../config/api';

// Animation variants for modern landing page effects
const fadeUpVariants: any = {
  hidden: { 
    opacity: 0, 
    y: 60,
    scale: 0.95
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1
  }
};

const staggerContainer: any = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1
  }
};

const slideInVariants: any = {
  hidden: { 
    opacity: 0, 
    x: -50,
    scale: 0.9
  },
  visible: { 
    opacity: 1, 
    x: 0,
    scale: 1
  }
};

const scaleVariants: any = {
  hidden: { 
    opacity: 0, 
    scale: 0.8,
    y: 20
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0
  }
};

const pulseVariants: any = {
  hover: {
    scale: 1.05
  },
  tap: {
    scale: 0.95
  }
};

const floatingVariants: any = {
  animate: {
    y: [-10, 10, -10]
  }
};

// Scroll reveal component
const ScrollReveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: { 
          opacity: 1, 
          y: 0
        }
      }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const ConsultantDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const [consultant, setConsultant] = useState<Consultant | null>(null);
  const [services, setServices] = useState<ConsultantService[]>([]);
  const [creneaux, setCreneaux] = useState<Record<string, Creneau[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Reset description expansion when consultant changes
  useEffect(() => {
    setIsDescriptionExpanded(false);
  }, [consultant?.id]);

  // Memoize description text calculation
  const descriptionDisplay = useMemo(() => {
    const descriptionText = consultant?.description || consultant?.about || '';
    if (!descriptionText) return { text: '', shouldTruncate: false };
    
    const maxLength = 200;
    const shouldTruncate = descriptionText.length > maxLength;
    const truncatedText = descriptionText.substring(0, maxLength) + '...';
    const fullText = descriptionText;
    
    return {
      text: isDescriptionExpanded ? fullText : (shouldTruncate ? truncatedText : fullText),
      shouldTruncate,
      fullText,
      truncatedText
    };
  }, [consultant?.description, consultant?.about, isDescriptionExpanded]);


  useEffect(() => {
    if (id) {
      loadConsultantData();
    }
  }, [id]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const loadConsultantData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [consultantData, servicesData] = await Promise.all([
        consultantsService.getConsultant(parseInt(id!)),
        consultantsService.getAllServices({ consultant_id: parseInt(id!) })
      ]);

      setConsultant(consultantData);
      setServices(servicesData.services);

      // Load creneaux for each service (for displaying available creneaux count)
      const creneauxPromises = servicesData.services.map(async (service) => {
        try {
          const creneauxData = await creneauxService.getCreneauxByService(service.id);
          return { serviceId: service.id, creneaux: creneauxData };
        } catch (error) {
          return { serviceId: service.id, creneaux: [] };
        }
      });

      const creneauxResults = await Promise.all(creneauxPromises);
      const creneauxMap: Record<string, Creneau[]> = {};
      creneauxResults.forEach(({ serviceId, creneaux }) => {
        creneauxMap[serviceId] = creneaux;
      });
      setCreneaux(creneauxMap);

    } catch (err) {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const canBookCreneau = (creneau: Creneau) => {
    // Allow booking if available, not booked, and not approved
    // Rejected créneaux become available again for booking
    return creneau.is_available && 
           !creneau.is_booked && 
           creneau.booking_status !== 'approved';
  };

  // Horizontal service card for consultant page
  const ServiceListItem = ({ service }: { service: ConsultantService }) => {
    const serviceCreneaux = creneaux[service.id] || [];
    const availableCreneaux = serviceCreneaux.filter(c => canBookCreneau(c));
    
    return (
      <motion.div
        onClick={() => navigate(`/service/${service.id}`)}
        className="bg-white rounded-lg sm:rounded-xl shadow-md border border-gray-200 overflow-hidden cursor-pointer hover:shadow-xl hover:border-brand-primary/30 transition-all duration-300 group flex flex-col sm:flex-row touch-manipulation"
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Image */}
        {service.banner_image ? (
          <div className="w-full sm:w-40 md:w-48 h-40 sm:h-48 md:h-auto overflow-hidden flex-shrink-0">
            <img
              src={service.banner_image.startsWith('http') ? service.banner_image : `${API_ENDPOINTS.UPLOADS}/${service.banner_image}`}
              alt={service.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                e.currentTarget.src = 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop';
              }}
            />
          </div>
        ) : (
          <div className="w-full sm:w-40 md:w-48 h-40 sm:h-48 md:h-auto bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 flex items-center justify-center flex-shrink-0">
            <Briefcase className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-brand-primary/50" />
          </div>
        )}
        
        {/* Content */}
        <div className="flex-1 p-3 sm:p-4 md:p-6 flex flex-col min-w-0">
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2 group-hover:text-brand-primary transition-colors line-clamp-2 break-words">
              {service.title}
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 line-clamp-2 sm:line-clamp-3 break-words">
              {service.full_description}
            </p>
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3 flex-wrap">
              <div className="flex items-center gap-1">
                <Clock3 className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate">{service.estimated_duration || t('serviceDetail.flexibleDuration', 'Durée flexible')}</span>
              </div>
              <div className="flex items-center gap-1">
                <Eye className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>{service.views || 0} {t('public.consultantDetail.views', 'vues')}</span>
              </div>
              {availableCreneaux.length > 0 && (
                <div className="flex items-center gap-1 text-brand-primary">
                  <Calendar className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span>{availableCreneaux.length} {t('consultant.calendar.slots', 'créneaux')}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-gray-100 gap-2">
            <div className="flex gap-1 flex-wrap flex-1 min-w-0">
              {service.format && (
                <span className="px-2 py-1 bg-brand-primary/10 text-brand-primary text-xs rounded whitespace-nowrap">
                  {service.format}
                </span>
              )}
              {service.language && (
                <span className="px-2 py-1 bg-brand-mint/10 text-brand-mint text-xs rounded whitespace-nowrap">
                  {service.language}
                </span>
              )}
            </div>
            <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 group-hover:text-brand-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
          </div>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-surface flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-primary/20"></div>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-primary border-t-transparent absolute top-0 left-0"></div>
          </div>
          <p className="text-gray-600 mt-4 text-lg font-medium">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !consultant) {
    return (
      <div className="min-h-screen bg-brand-surface flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-24 h-24 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <User className="h-12 w-12 text-brand-primary" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('consultant.notFound.title')}</h2>
          <p className="text-gray-600 mb-8 text-lg">{error || t('consultant.notFound.subtitle')}</p>
          <div className="flex gap-4 justify-center">
            <motion.button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-brand-primary to-purple-600 text-white px-8 py-3 rounded-xl hover:from-purple-600 hover:to-brand-primary transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              variants={pulseVariants}
              whileHover="hover"
              whileTap="tap"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {t('common.backToHome')}
            </motion.button>
            <motion.button
              onClick={() => navigate('/consultants')}
              className="border-2 border-brand-secondary text-brand-secondary px-8 py-3 rounded-xl hover:bg-brand-secondary hover:text-white transition-all duration-200 font-semibold"
              variants={pulseVariants}
              whileHover="hover"
              whileTap="tap"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {t('consultant.viewConsultants')}
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-surface">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-3 text-gray-600 hover:text-brand-primary transition-colors font-medium"
            >
              <ArrowLeft className="h-5 w-5" />
              {t('common.back')}
            </button>
            
            <div className="flex items-center gap-4">
              <button className="p-3 text-gray-600 hover:text-brand-secondary transition-colors hover:bg-brand-secondary/5 rounded-xl">
                <Share2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Consultant Hero Section */}
            <motion.div 
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-8"
              initial="hidden"
              animate="visible"
              variants={fadeUpVariants}
              transition={{
                duration: 0.8,
                ease: "easeOut",
                staggerChildren: 0.1
              }}
            >
              {/* Consultant Banner */}
              <div className="min-h-48 sm:min-h-56 md:min-h-64 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 relative overflow-hidden py-4 sm:py-6 md:py-8">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 md:gap-6 px-4 sm:px-6 md:px-8">
                  {/* Logo */}
                  <div className="flex-shrink-0">
                    {consultant.logo ? (
                      <motion.img
                        src={consultant.logo.startsWith('http') ? consultant.logo : `${API_ENDPOINTS.UPLOADS}/${consultant.logo}`}
                        alt={consultant.name}
                        className="w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 object-cover rounded-2xl"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                          if (nextElement) {
                            nextElement.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div 
                      className="w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 bg-gradient-to-r from-brand-primary to-purple-600 rounded-2xl flex items-center justify-center shadow-lg hidden"
                      style={{ display: consultant.logo ? 'none' : 'flex' }}
                    >
                      <User className="h-14 w-14 sm:h-16 sm:w-16 md:h-18 md:w-18 lg:h-20 lg:w-20 text-white" />
                    </div>
                  </div>
                  
                  {/* Consultant Info */}
                  <div className="flex-1 min-w-0 w-full sm:w-auto">
                    <motion.h1 
                      className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-3 leading-tight break-words"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                    >
                      {consultant.name}
                    </motion.h1>
                    <motion.div 
                      className="flex items-center gap-3 mb-3 sm:mb-4"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                    >
                      <motion.div 
                        className="w-10 h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        <GraduationCap className="h-5 w-5 text-white" />
                      </motion.div>
                      <div className="min-w-0 flex-1">
                        <p className="text-base sm:text-lg md:text-xl font-semibold text-gray-800">
                          {t('public.consultantDetail.professional', 'Consultant Professionnel')}
                        </p>
                        <p className="text-sm sm:text-base text-gray-600">
                          {consultant.specialties && consultant.specialties.length > 0 
                            ? `${t('public.consultantDetail.expert', 'Expert')} ${consultant.specialties.join(', ')}` 
                            : t('public.consultantDetail.expert', 'Expert consultant professionnel')
                          }
                        </p>
                      </div>
                    </motion.div>
                    
                    {/* Consultant Description */}
                    {descriptionDisplay.text && (
                      <motion.div
                        className="mb-3 sm:mb-4"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                      >
                        <div 
                          key={`desc-text-${isDescriptionExpanded ? 'expanded' : 'collapsed'}`}
                          className="text-gray-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words"
                        >
                          {descriptionDisplay.text}
                        </div>
                        {descriptionDisplay.shouldTruncate && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsDescriptionExpanded(prev => !prev);
                            }}
                            className="mt-2 text-brand-primary hover:text-brand-secondary font-medium text-sm sm:text-base transition-colors flex items-center gap-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/20 rounded px-1"
                          >
                            {isDescriptionExpanded ? (
                              <>
                                <span>{t('public.consultantDetail.readLess', 'Lire moins')}</span>
                                <ChevronUp className="h-4 w-4" />
                              </>
                            ) : (
                              <>
                                <span>{t('public.consultantDetail.readMore', 'Lire plus')}</span>
                                <ChevronDown className="h-4 w-4" />
                              </>
                            )}
                          </button>
                        )}
                      </motion.div>
                    )}
                    
                    {/* Additional Info */}
                    <motion.div 
                      className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.7 }}
                    >
                      {consultant.country && (
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-brand-primary" />
                          <span className="font-medium">{typeof consultant.country === 'object' ? consultant.country.name : consultant.country}</span>
                        </div>
                      )}
                      {consultant.wilaya && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-brand-secondary" />
                          <span className="font-medium">{typeof consultant.wilaya === 'object' ? consultant.wilaya.name : consultant.wilaya}</span>
                        </div>
                      )}
                      {consultant.city && (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-brand-accent" />
                          <span className="font-medium">{consultant.city}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-purple-600" />
                        <span className="font-medium">{consultant.views || 0} {t('public.consultantDetail.views', 'vues')}</span>
                      </div>
                      {consultant.rating > 0 && (
                        <div className="flex items-center gap-2">
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          <span className="font-medium">{consultant.rating.toFixed(1)} ({consultant.reviews_count || consultant.reviewCount || 0} {t('consultantDetail.reviews', 'avis')})</span>
                        </div>
                      )}
                      {consultant.verification_status === 'verified' && (
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-green-600">{t('public.consultantDetail.verified', 'Vérifié')}</span>
                        </div>
                      )}
                      {consultant.is_featured && (
                        <div className="flex items-center gap-2">
                          <Award className="h-4 w-4 text-brand-accent" />
                          <span className="font-medium text-brand-accent">{t('public.consultantDetail.featured', 'Mis en vedette')}</span>
                        </div>
                      )}
                    </motion.div>

                    {/* Social Links */}
                    <motion.div 
                      className="flex items-center gap-3 sm:gap-4 flex-wrap"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: 0.6 }}
                    >
                      {consultant.facebook_url && (
                        <a href={consultant.facebook_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors text-xs sm:text-sm whitespace-nowrap">
                          <Facebook className="h-4 w-4 sm:h-5 sm:w-5" />
                          <span className="font-medium">{t('public.consultantDetail.facebook', 'Facebook')}</span>
                        </a>
                      )}
                      {consultant.linkedin_url && (
                        <a href={consultant.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-600 hover:text-blue-700 transition-colors text-xs sm:text-sm whitespace-nowrap">
                          <Linkedin className="h-4 w-4 sm:h-5 sm:w-5" />
                          <span className="font-medium">{t('public.consultantDetail.linkedin', 'LinkedIn')}</span>
                        </a>
                      )}
                      {consultant.website && (
                        <a href={consultant.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-600 hover:text-brand-primary transition-colors text-xs sm:text-sm whitespace-nowrap">
                          <Globe className="h-4 w-4 sm:h-5 sm:w-5" />
                          <span className="font-medium">{t('public.consultantDetail.website', 'Site web')}</span>
                        </a>
                      )}
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Additional Details Section - Only show if there's content */}
              {((consultant.company_name && consultant.company_name !== consultant.name) || consultant.totalTrainings !== undefined) && (
                <div className="px-6 sm:px-8 py-6 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {consultant.company_name && consultant.company_name !== consultant.name && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-500 mb-1">{t('public.consultantDetail.companyName', 'Nom de l\'entreprise')}</h4>
                        <p className="text-base font-medium text-gray-900">{consultant.company_name}</p>
                      </div>
                    )}
                    {consultant.totalTrainings !== undefined && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-500 mb-1">{t('public.consultantDetail.numberOfServices', 'Nombre de prestations')}</h4>
                        <p className="text-base font-medium text-gray-900">{consultant.totalTrainings}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            
            </motion.div>

            {/* Prestations disponibles */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6 mb-4 sm:mb-6 md:mb-8">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3 flex-wrap">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-brand-mint to-teal-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                  <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <span className="break-words">{t('public.consultantDetail.availableServices', 'Prestations disponibles')} ({services.length})</span>
              </h3>
              
              <div className="space-y-3 sm:space-y-4">
                {services.length > 0 ? (
                  services.map((service) => (
                    <ServiceListItem key={service.id} service={service} />
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">{t('public.consultantDetail.noServicesAvailable', 'Aucune prestation disponible')}</p>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4 sm:space-y-6">
            {/* Contact Card */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <span className="break-words">{t('common.contact')}</span>
              </h3>
              
              <div className="space-y-3 sm:space-y-4">
                {consultant.phone_number && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 bg-gradient-to-r from-brand-mint to-teal-600 rounded-lg flex items-center justify-center">
                    <Phone className="h-4 w-4 text-white" />
                  </div>
                    <a href={`tel:${consultant.phone_number}`} className="font-medium text-gray-700 hover:text-brand-primary transition-colors">
                      {consultant.phone_number}
                    </a>
                </div>
                )}

                {consultant.user?.email && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 bg-gradient-to-r from-brand-secondary to-pink-600 rounded-lg flex items-center justify-center">
                    <Mail className="h-4 w-4 text-white" />
                  </div>
                    <a href={`mailto:${consultant.user.email}`} className="font-medium text-gray-700 hover:text-brand-primary transition-colors">
                      {consultant.user.email}
                    </a>
                </div>
                )}

                {consultant.address && (
                <div className="flex items-start gap-3 text-sm">
                  <div className="w-8 h-8 bg-gradient-to-r from-brand-accent to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MapPin className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium text-gray-700 break-words flex-1 min-w-0">{consultant.address}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Info Card */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-brand-mint to-teal-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                  <UserCheck className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <span className="break-words">{t('public.consultantDetail.additionalInfo', 'Informations complémentaires')}</span>
              </h3>
              
              <div className="space-y-3 sm:space-y-4">
                {consultant.created_at && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 bg-gradient-to-r from-brand-primary to-purple-600 rounded-lg flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-0.5">
                        {t('public.consultantDetail.memberSince', 'Membre depuis')}
                      </p>
                      <p className="font-medium text-gray-700">
                        {new Date(consultant.created_at).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { 
                          year: 'numeric', 
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                )}
                
                {consultant.specialties && consultant.specialties.length > 0 && (
                  <div className="flex items-start gap-3 text-sm">
                    <div className="w-8 h-8 bg-gradient-to-r from-brand-accent to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-500 mb-2">
                        {t('public.consultantDetail.specialties', 'Spécialités')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {consultant.specialties.map((specialty, index) => (
                          <span key={index} className="font-medium text-gray-700">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantDetailPage;